import { useContext } from "react";
import {
  DateState,
  EmailState,
  LocationState,
  MultiState,
  QuizCtx,
  SelectorState,
  ShortTextState,
  SingleState,
  TimeState,
  useQuizActions,
  useQuizSnapshot,
} from "../internal/state";
import { QuizQuestionsState } from "./slide";

/** Non-reactive usage */
export function useQuizContext() {
  const c = useContext(QuizCtx).state.slideStateByID;
  return c;
}

export function useQuiz() {
  const actions = useQuizActions();
  return {
    submitQuestion() {
      return actions.submitQuestion();
    },
    skipQuestion() {
      return actions.skipQuestion();
    },
    goToNext() {
      return actions.goToNext();
    },
    goToSlideIdx(idx: number) {
      return actions.goToSlideIdx(idx);
    },
    checkQuestion() {
      return actions.checkQuestion();
    },
  };
}

export function useQuizState() {
  const snap = useQuizSnapshot();

  return {
    quizState: snap.slideStateByID,
  };
}

export function useSlideState<T extends SelectorState>() {
  const s = useQuizSnapshot();
  const slideState = s.currentSlideID ? s.slideStateByID[s.currentSlideID] : undefined;
  return slideState as T;
}

export function useQuizValues<T extends Record<string, any>>(): T {
  const s = useQuizSnapshot();
  return getQuizValues<T>(s.slideStateByID);
}

export function getQuizValues<T extends Record<string, any>>(s: QuizQuestionsState): T {
  const result = {} as Record<string, any>;

  for (const key in s) {
    const state = s[key];
    if (state.type === "single") {
      result[key] = (state as SingleState).value?.value;
    } else if (state.type === "multi") {
      result[key] = (state as MultiState).value?.map((it) => it.value) ?? [];
    } else if (state.type === "email") {
      result[key] = (state as EmailState).value;
    } else if (state.type === "short-text") {
      result[key] = (state as ShortTextState).value;
    } else if (state.type === "location") {
      result[key] = (state as LocationState).value;
    } else if (state.type === "date") {
      result[key] = (state as DateState).value;
    } else if (state.type === "time") {
      result[key] = (state as TimeState).value;
    }
  }

  return result as T;
}
